import { Routes, Route, Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { Authenticator, ThemeProvider, View, useTheme, type Theme } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { signIn } from 'aws-amplify/auth';
import { emailDomainProblem } from './lib/auth/emailFormat';
import { type ReactNode, useState, useRef, useEffect, lazy, Suspense } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faGaugeHigh, faFingerprint, faReceipt, faStore, faUser, faCreditCard, faMicrochip,
  faTruckFast, faShieldHalved, faUsers, faDiagramProject, faCircleQuestion,
  faCartShopping, faRightFromBracket, faDownload, faPaperPlane, faChartLine, faIdCard,
  faLifeRing, faInbox, faBook, faCompass, faRulerCombined, faPrint, faFileLines, faFileInvoiceDollar,
} from '@fortawesome/free-solid-svg-icons';
import { Landing } from './features/marketing/Landing';
import { Moved } from './features/marketing/Moved';
import { Terms, Privacy, Returns } from './features/legal/Legal';
import V1Recovery from './features/auth/V1Recovery';
import { AccountConsole } from './features/account/AccountConsole';
import { MyProfile } from './features/account/MyProfile';
import { AcceptInvite } from './features/account/AcceptInvite';
import { RequireAccount } from './features/account/RequireAccount';
import { readInvitation, type Invitation } from './lib/account/invitation';
import { SupportWidget, useSupport } from './features/support/SupportWidget';
import { DeviceServiceStatusProvider } from './lib/deviceService/status';
import { Users } from './features/account/Users';
import { Store } from './features/store/Store';
import { Orders } from './features/store/Orders';
import { CartPage } from './features/store/CartPage';
import { CartProvider, useCart } from './features/store/cart';
import { NewTransaction } from './features/capture/NewTransaction';
import { CaptureFlow } from './features/capture/CaptureFlow';
import { Help } from './features/help/Help';
import { DeviceStatus } from './features/help/DeviceStatus';
import { BarcodeScanner } from './features/help/BarcodeScanner';
import { usePrintEft } from './features/help/PrintEft';
import { Transactions } from './features/records/Transactions';
import { EditTransaction } from './features/records/EditTransaction';
import { CorrectEft } from './features/records/CorrectEft';
import { Subscriptions } from './features/billing/Subscriptions';
import { Devices } from './features/devices/Devices';
import { Customers, CustomerDetailPage } from './features/admin/Customers';
import { Workflows, WorkflowDetailPage } from './features/admin/Workflows';
import { RuleLibrary, RuleGroupDetailPage } from './features/admin/RuleLibrary';
import { Specifications, SpecificationDetailPage } from './features/admin/Specifications';
import { Unmigrated } from './features/admin/Unmigrated';
import { useAppVersion } from './lib/useAppVersion';
import { StoreProducts } from './features/admin/StoreProducts';
import { AdminOrders } from './features/admin/AdminOrders';
import { Downloads } from './features/downloads/Downloads';
import { Downloads as DownloadsAdmin } from './features/admin/Downloads';
import { Newsletter } from './features/admin/Newsletter';
import { MyTickets } from './features/support/MyTickets';
import { SupportConsole, SupportTicketPage } from './features/admin/SupportConsole';
import { KnowledgeBase } from './features/admin/KnowledgeBase';
import { DoceCustomers } from './features/admin/DoceCustomers';
import { DoceTransactions } from './features/admin/DoceTransactions';
const Operations = lazy(() => import('./features/admin/Operations'));
import { TourProvider } from './features/tour/TourProvider';
import { ThemeToggle } from './components/ThemeToggle';
import { useTour } from './features/tour/context';
import type { TourMenu } from './features/tour/steps';
import { useIsSiteAdmin, useCan, useClaims } from './lib/api/hooks';
import { LogoImage } from './components/Logo';
import './styles/app.css';

// Map Amplify UI tokens onto the EFTCreator dark palette.
const authTheme: Theme = {
  name: 'eftc-dark',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: '#04231d', 20: '#083a30', 40: '#1a9d84', 60: '#2fe6c4',
          80: '#55f2d6', 90: '#8ff8e6', 100: '#c9fdf3',
        },
      },
    },
    components: {
      button: {
        primary: {
          backgroundColor: '{colors.brand.primary.60}',
          color: '#04231d',
          _hover: { backgroundColor: '{colors.brand.primary.80}', color: '#04231d' },
        },
      },
    },
  },
};

/**
 * The invitation in the URL, if this is an invite link.
 *
 * Read from `window.location` rather than the router: the authenticator sits ABOVE the routes, so it must
 * decide which form to show before any route has matched. Parsing lives in `readInvitation` so it can be
 * tested — this wrapper is only the window lookup.
 */
function invitationInUrl(): Invitation | null {
  if (typeof window === 'undefined') return null;
  return readInvitation(window.location.pathname, window.location.search);
}

function AuthHeader() {
  const { tokens } = useTheme();
  return (
    <View textAlign="center" padding={`${tokens.space.xl} 0 ${tokens.space.small}`}>
      <Link to="/" style={{ display: 'inline-block' }}><LogoImage height={38} /></Link>
      <p className="auth-tagline">Biometric EFT submission platform</p>
      {/*
        * Returning v1 customers sign in here with the credentials they already have — Cognito's UserMigration
        * trigger moves them across on first sign-in, no reset email.
        *
        * The emphasis on the EXACT address is not padding: their transactions, plans and credits are found by
        * matching it, so signing up fresh with a variant (a personal address, a plus-tag, a different domain)
        * produces a working but empty account, which is far harder to explain afterwards than to prevent here.
        */}
      {invitationInUrl() ? (
        /*
          * An invitee is not a returning v1 customer, and the notice below tells them to sign in with an
          * account they do not have. Worse, it is the "sign in" framing that sent them to the login form
          * looking for a password nobody ever gave them.
          */
        <div className="auth-notice">
          <strong>You've been invited to join a team</strong>
          <span>
            Create a password below to set up your sign-in, then you'll be shown the account you're joining.
            Use the <b>same email address the invitation was sent to</b> — the invitation only works for that
            address.
          </span>
          <span className="auth-notice-help">
            Already have an EFTCreator sign-in? Choose <b>Sign In</b> instead and use it.
          </span>
        </div>
      ) : (
      <div className="auth-notice">
        <strong>Already have an EFTCreator account?</strong>
        <span>
          Sign in with the <b>same email address and password you use today</b> — there is nothing to set up and
          no need to register again. Your transactions, plan and devices come across on your first sign-in.
        </span>
        <span className="auth-notice-warn">
          Use the address <b>exactly</b> as it appears on your existing account. A different or misspelled
          address creates a new, empty account instead of moving your history.
        </span>
        {/*
          * TEMPORARY (remove with the v1 decommission): the authenticator's own "Forgot your password?"
          * cannot help someone who has not moved across yet — they do not exist in this pool, so Cognito
          * reports a code was sent and none is. This link goes to the reset that works for them.
          */}
        <span className="auth-notice-help">
          Forgotten the password for your existing account?{' '}
          <Link to="/v1-recovery">Reset it here</Link> before signing in.
        </span>
      </div>
      )}
    </View>
  );
}

/**
 * "A newer version is available."
 *
 * Reload is offered rather than forced. An operator mid-capture has fingerprints staged and demographics
 * typed that a reload would discard, so taking that decision away from them would trade one support call for
 * a worse one. It is persistent instead — it does not dismiss, because the reason it exists is that people
 * carried on for days without knowing.
 */
function UpdateBanner() {
  const { stale, deployed } = useAppVersion();
  if (!stale) return null;
  return (
    <div className="update-banner" role="status">
      <span>
        A newer version of EFTCreator has been released{deployed ? <> (<span className="mono">{deployed}</span>)</> : null}.
        Reload to pick it up — finish anything in progress first.
      </span>
      <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>Reload now</button>
    </div>
  );
}

function AppShell({ children, onSignOut }: { children: ReactNode; onSignOut?: () => void }) {
  /*
   * The primary navigation is seventeen links. On a desktop bar they fit; on a phone they would wrap
   * into a wall or push the page sideways, so below the breakpoint they collapse behind a toggle.
   *
   * Closed on navigation, because a full-height menu that stays open over the page the operator just
   * asked for is the most common way this pattern goes wrong.
   */
  const [navOpen, setNavOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setNavOpen(false); }, [pathname]);

  return (
    <div className={`app${navOpen ? ' nav-open' : ''}`}>
      {/* Above the bar, so it is the first thing on the page rather than something to scroll to. */}
      <UpdateBanner />
      <header className="app-bar">
        <div className="app-bar-inner">
          <div className="app-bar-lead">
            <button
              type="button"
              className="nav-toggle"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={navOpen}
              onClick={() => setNavOpen((v) => !v)}
            >
              <span aria-hidden>{navOpen ? '✕' : '☰'}</span>
            </button>
            <Link to="/app" aria-label="EFTCreator home"><LogoImage height={28} /></Link>
            <PrimaryNav />
          </div>
          <nav className="app-nav app-nav-end">
            <CartNavLink />
            <UserMenu onSignOut={onSignOut} />
          </nav>
        </div>
      </header>
      <DeviceServiceStatusProvider>
        {/* The footer lives inside SupportWidget so its Support link can open the Assistant — useSupport
            throws outside the provider. */}
        <SupportWidget>
          <main className="app-main">{children}</main>
          <AppFooter />
        </SupportWidget>
      </DeviceServiceStatusProvider>
    </div>
  );
}

/** Global footer — legal links + copyright, present on every signed-in page. */
function AppFooter() {
  const { openSupport } = useSupport();
  return (
    <footer className="app-footer">
      <div className="app-footer-inner">
        <span className="app-footer-copy">© {new Date().getFullYear()} Lakota Software Solutions, Inc.</span>
        <nav className="app-footer-links">
          <Link to="/legal/terms">Terms of Use</Link>
          <Link to="/legal/privacy">Privacy Policy</Link>
          <Link to="/legal/returns">Return Policy</Link>
          {/* Opens the Assistant rather than an email client: it answers most questions from the KB and
              opens a real ticket when it can't, which a mailto bypasses entirely. */}
          <button type="button" onClick={openSupport}>Support</button>
        </nav>
      </div>
    </footer>
  );
}

/** One dropdown entry: a route link, or an in-place action (e.g. launching the guided tour). */
interface NavItem {
  label: string;
  icon?: IconDefinition;
  to?: string;
  action?: () => void;
  /** `data-tour` hook so the guided tour can spotlight this entry. */
  tour?: string;
}

/**
 * A labelled group within a dropdown.
 *
 * Sections rather than nested fly-out menus. The admin menu had grown to thirteen flat entries and needed
 * structure, but a submenu that opens on hover costs a second click or a steady hand for every destination
 * behind it, and behaves badly on a touch screen. A heading groups them while leaving everything exactly
 * one click away, which is what the length was costing in the first place.
 */
interface NavHeading {
  heading: string;
}

type NavEntry = NavItem | NavHeading;

const isHeading = (e: NavEntry): e is NavHeading => 'heading' in e;

/** A grouped nav menu (click to open, closes on outside-click or selection). */
function NavDropdown({ label, items, match, icon, menuId }: {
  label: string; items: NavEntry[]; match: string; icon?: IconDefinition; menuId?: TourMenu;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { openMenu } = useTour();
  const active = new RegExp(match).test(location.pathname);
  // The tour holds a menu open while it spotlights one of its entries.
  const isOpen = open || (menuId != null && openMenu === menuId);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  return (
    <div className="nav-dd" ref={ref}>
      <button type="button" className={`app-nav-link nav-dd-btn${active ? ' is-active' : ''}${isOpen ? ' is-open' : ''}`}
        aria-haspopup="true" aria-expanded={isOpen} onClick={() => setOpen((o) => !o)}>
        {icon && <FontAwesomeIcon icon={icon} className="nav-ico" fixedWidth />}{label}
        <span className="nav-dd-caret" aria-hidden>▾</span>
      </button>
      {isOpen && (
        <div className="nav-dd-menu" role="menu">
          {items.map((entry) => isHeading(entry) ? (
            // `presentation`, not `menuitem`: a heading is not something to choose, and announcing it as
            // one would make a screen reader count it among the options.
            <div key={`h-${entry.heading}`} className="nav-dd-heading" role="presentation">{entry.heading}</div>
          ) : entry.to !== undefined ? (
            <NavLink key={entry.label} to={entry.to} role="menuitem" data-tour={entry.tour}
              className={({ isActive }) => `nav-dd-item${isActive ? ' is-active' : ''}`}>
              {entry.icon && <FontAwesomeIcon icon={entry.icon} className="nav-ico" fixedWidth />}{entry.label}
            </NavLink>
          ) : (
            <button key={entry.label} type="button" role="menuitem" data-tour={entry.tour} className="nav-dd-item nav-dd-action"
              onClick={() => { setOpen(false); entry.action?.(); }}>
              {entry.icon && <FontAwesomeIcon icon={entry.icon} className="nav-ico" fixedWidth />}{entry.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminMenu() {
  const isAdmin = useIsSiteAdmin();
  if (!isAdmin) return null;
  return (
    <NavDropdown label="Admin" icon={faShieldHalved} match="/app/admin" items={[
      { to: '/app/admin/operations', label: 'Operations', icon: faChartLine },
      { to: '/app/admin/customers', label: 'Customer management', icon: faUsers },
      { to: '/app/admin/downloads', label: 'Download management', icon: faDownload },
      { to: '/app/admin/newsletter', label: 'Newsletter', icon: faPaperPlane },
      { heading: 'Reports' },
      { to: '/app/admin/reports/doce', label: 'DOCE customers', icon: faFileLines },
      { to: '/app/admin/reports/doce-transactions', label: 'DOCE transactions', icon: faFileInvoiceDollar },
      { to: '/app/admin/unmigrated', label: 'Unmigrated v1 customers', icon: faUsers },
      { heading: 'Workflows' },
      { to: '/app/admin/workflows', label: 'Transaction workflows', icon: faDiagramProject },
      { to: '/app/admin/rule-library', label: 'Rules library', icon: faDiagramProject },
      { to: '/app/admin/specs', label: 'EBTS specifications', icon: faRulerCombined },
      { heading: 'Store' },
      { to: '/app/admin/store', label: 'Store products', icon: faStore },
      { to: '/app/admin/orders', label: 'Orders', icon: faTruckFast },
      { heading: 'Support' },
      { to: '/app/admin/support', label: 'Support inbox', icon: faInbox },
      { to: '/app/admin/kb', label: 'Knowledge base', icon: faBook },
    ]} />
  );
}

/** Left-side primary navigation, capability-gated to the caller's role. */
function PrimaryNav() {
  const can = useCan();
  const { start } = useTour();
  const printEft = usePrintEft();
  return (
    <nav className="app-nav">
      <NavLink end to="/app" className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}><FontAwesomeIcon icon={faGaugeHigh} className="nav-ico" fixedWidth />Dashboard</NavLink>
      {can('Capture') && (
        <NavLink to="/app/capture" data-tour="nav-capture" className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}><FontAwesomeIcon icon={faFingerprint} className="nav-ico" fixedWidth />New EFT</NavLink>
      )}
      <NavLink to="/app/transactions" data-tour="nav-transactions" className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}><FontAwesomeIcon icon={faReceipt} className="nav-ico" fixedWidth />Transactions</NavLink>
      <NavLink to="/app/store" data-tour="nav-store" className={({ isActive }) => `app-nav-link${isActive ? ' is-active' : ''}`}><FontAwesomeIcon icon={faStore} className="nav-ico" fixedWidth />Store</NavLink>
      <NavDropdown label="Resources" icon={faCircleQuestion} menuId="resources" match="/app/(downloads|help|support)" items={[
        { to: '/app/downloads', label: 'Downloads', icon: faDownload, tour: 'nav-downloads' },
        { to: '/app/help/device', label: 'Device status', icon: faMicrochip, tour: 'nav-device-status' },
        { to: '/app/help/barcode-scanner', label: 'Barcode scanner setup', icon: faIdCard },
        { label: 'Print', icon: faPrint, action: printEft.print },
        { to: '/app/help', label: 'Help center', icon: faCircleQuestion },
        { to: '/app/support', label: 'Support', icon: faLifeRing },
        { label: 'Getting started', icon: faCompass, action: start },
      ]} />
      <AdminMenu />
      {printEft.dialog}
    </nav>
  );
}

/** Signed-in user menu — profile, account admin items (role-gated), and sign out. */
function UserMenu({ onSignOut }: { onSignOut?: (() => void) | undefined }) {
  const claims = useClaims();
  const can = useCan();
  const { openMenu } = useTour();
  const email = (claims.data?.['email'] as string | undefined) ?? 'Account';
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isOpen = open || openMenu === 'user'; // the tour holds this open to show what's inside
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  const acctItems: { to: string; label: string; icon: IconDefinition }[] = [];
  if (can('Billing')) acctItems.push({ to: '/app/subscriptions', label: 'Subscriptions & billing', icon: faCreditCard });
  if (can('ManageDevices')) acctItems.push({ to: '/app/devices', label: 'Devices & licenses', icon: faMicrochip });
  if (can('ManageUsers')) acctItems.push({ to: '/app/users', label: 'Team & users', icon: faUsers });
  return (
    <div className="nav-dd" ref={ref}>
      <button type="button" data-tour="nav-user" className={`app-nav-link nav-dd-btn${isOpen ? ' is-open' : ''}`} aria-haspopup="true" aria-expanded={isOpen} onClick={() => setOpen((o) => !o)}>
        <FontAwesomeIcon icon={faUser} className="nav-ico" fixedWidth /><span className="nav-dd-caret" aria-hidden>▾</span>
      </button>
      {isOpen && (
        <div className="nav-dd-menu" data-tour="user-menu" role="menu">
          <div style={{ padding: '8px 16px', fontSize: 12.5, color: 'var(--text-dim)', borderBottom: '1px solid var(--border)', wordBreak: 'break-all' }}>{email}</div>
          <NavLink to="/app/profile" role="menuitem" className={({ isActive }) => `nav-dd-item${isActive ? ' is-active' : ''}`}>
            <FontAwesomeIcon icon={faIdCard} className="nav-ico" fixedWidth />My Profile
          </NavLink>
          <NavLink to="/app/orders" role="menuitem" className={({ isActive }) => `nav-dd-item${isActive ? ' is-active' : ''}`}>
            <FontAwesomeIcon icon={faTruckFast} className="nav-ico" fixedWidth />My orders
          </NavLink>
          {acctItems.map((it) => (
            <NavLink key={it.to} to={it.to} role="menuitem" className={({ isActive }) => `nav-dd-item${isActive ? ' is-active' : ''}`}>
              <FontAwesomeIcon icon={it.icon} className="nav-ico" fixedWidth />{it.label}
            </NavLink>
          ))}
          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
          {/* In the user menu rather than the app bar: it is a personal preference, set once, and does not
              deserve permanent space beside the things people use every day. */}
          <div className="nav-dd-theme">
            <span className="nav-dd-theme-label">Theme</span>
            <ThemeToggle />
          </div>
          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
          <button type="button" role="menuitem" className="nav-dd-item" onClick={onSignOut}
            style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, cursor: 'pointer', font: 'inherit' }}>
            <FontAwesomeIcon icon={faRightFromBracket} className="nav-ico" fixedWidth />Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function CartNavLink() {
  const { count } = useCart();
  if (count === 0) return null; // contextual — only shown when the cart has items
  return (
    <NavLink to="/app/cart" className={({ isActive }) => `app-nav-link cart-nav${isActive ? ' is-active' : ''}`}>
      <FontAwesomeIcon icon={faCartShopping} className="nav-ico" fixedWidth />Cart<span className="cart-badge">{count}</span>
    </NavLink>
  );
}

function ProtectedApp() {
  /*
   * An invitee has no sign-in yet — that is the entire reason they were sent a link — so the authenticator
   * must open on CREATE ACCOUNT rather than its default sign-in form. It opened on sign-in, which asked an
   * invited teammate for a password that had never been issued and gave them nowhere to go.
   *
   * Computed once, outside render: it comes from the URL, and re-reading it after the authenticator has
   * mounted would fight the tab the user then chose for themselves.
   */
  const invite = invitationInUrl();
  /*
   * The invited address, filled in and locked.
   *
   * The field is keyed `email`, not `username`: the sign-up form's fields are
   * `[...loginMechanisms, password, confirm_password, ...signUpAttributes]` deduped, and both of ours are
   * `email`. A `username` key would not prefill anything — it would ADD a stray Username box.
   *
   * Locked because the invitation only works for the address it was sent to. Registering with a variant
   * produces a working but EMPTY account and leaves the invitation unaccepted, which is the same trap the
   * v1 sign-in notice warns about and is far harder to unpick afterwards than to prevent here. Read-only
   * rather than disabled: a disabled input is omitted from the submitted FormData entirely.
   *
   * Cast because `defaultValue` and `isReadOnly` are absent from Amplify's FormFieldOptions type while
   * being spread straight onto the underlying input, which honours both.
   */
  const inviteFormFields = invite?.email
    ? ({
        signUp: {
          email: {
            defaultValue: invite.email,
            isReadOnly: true,
            label: 'Email (from your invitation)',
          },
        },
      } as NonNullable<React.ComponentProps<typeof Authenticator>['formFields']>)
    : undefined;
  return (
    <div className="auth-page grain">
      <div className="auth-page-bg" />
      <ThemeProvider theme={authTheme} colorMode="dark">
        <Authenticator
          signUpAttributes={['email']}
          loginMechanisms={['email']}
          {...(invite ? { initialState: 'signUp' as const } : {})}
          {...(inviteFormFields ? { formFields: inviteFormFields } : {})}
          // Force USER_PASSWORD_AUTH (not SRP) so Cognito's UserMigration trigger fires for v1 users —
          // JIT migration only runs on USER_PASSWORD_AUTH.
          services={{
            handleSignIn: (input) =>
              signIn({ ...input, options: { ...(input.options ?? {}), authFlowType: 'USER_PASSWORD_AUTH' } }),
            // Cognito accepts any `x@y` as an email, so an address with no TLD gets an account and a
            // verification code that can never arrive. Checked here because sign-up talks to Cognito
            // directly — there is no request of ours in that path to validate server-side.
            validateCustomSignUp: async (formData) => {
              // With loginMechanisms={['email']} the address arrives as `username`; `email` is the
              // signUpAttribute. They are the same value, but which one is populated depends on the form.
              const problem =
                emailDomainProblem(String(formData['username'] ?? '')) ??
                emailDomainProblem(String(formData['email'] ?? ''));
              if (!problem) return;
              return { ...(formData['username'] ? { username: problem } : {}), ...(formData['email'] ? { email: problem } : {}) };
            },
          }}
          components={{ Header: AuthHeader }}
        >
          {({ signOut }) => (
            <CartProvider>
              <RequireAccount onSignOut={() => signOut?.()}>
                <TourProvider>
                <AppShell onSignOut={() => signOut?.()}>
                <Routes>
                  <Route index element={<AccountConsole />} />
                  <Route path="profile" element={<MyProfile />} />
                  {/* Invitation links land here; the authenticator gates it, so the invitee signs in first. */}
                  <Route path="accept-invite" element={<AcceptInvite />} />
                  <Route path="capture" element={<NewTransaction />} />
                  <Route path="capture/:type" element={<CaptureFlow />} />
                  <Route path="transactions" element={<Transactions />} />
                  <Route path="transactions/:tcn/edit" element={<EditTransaction />} />
                  <Route path="tools/correct-eft" element={<CorrectEft />} />
                  <Route path="devices" element={<Devices />} />
                  <Route path="users" element={<Users />} />
                  <Route path="subscriptions" element={<Subscriptions />} />
                  <Route path="admin/operations" element={<Suspense fallback={<div className="loading-screen">// loading operations…</div>}><Operations /></Suspense>} />
                  <Route path="admin/customers" element={<Customers />} />
                  <Route path="admin/customers/:accountId" element={<CustomerDetailPage />} />
                  <Route path="admin/workflows" element={<Workflows />} />
                  <Route path="admin/workflows/:captureType" element={<WorkflowDetailPage />} />
                  <Route path="admin/rule-library" element={<RuleLibrary />} />
                  <Route path="admin/rule-library/:groupId" element={<RuleGroupDetailPage />} />
                  <Route path="admin/specs" element={<Specifications />} />
                  <Route path="admin/specs/:spec" element={<SpecificationDetailPage />} />
                  <Route path="admin/unmigrated" element={<Unmigrated />} />
                  <Route path="admin/reports/doce" element={<DoceCustomers />} />
                  <Route path="admin/reports/doce-transactions" element={<DoceTransactions />} />
                  <Route path="admin/store" element={<StoreProducts />} />
                  <Route path="admin/orders" element={<AdminOrders />} />
                  <Route path="admin/downloads" element={<DownloadsAdmin />} />
                  <Route path="admin/newsletter" element={<Newsletter />} />
                  <Route path="admin/support" element={<SupportConsole />} />
                  <Route path="admin/support/:accountId/:ticketId" element={<SupportTicketPage />} />
                  <Route path="admin/kb" element={<KnowledgeBase />} />
                  <Route path="support" element={<MyTickets />} />
                  {/* /app/storage kept as a redirect target for old links */}
                  <Route path="storage" element={<Navigate to="/app/transactions" replace />} />
                  <Route path="store" element={<Store />} />
                  <Route path="downloads" element={<Downloads />} />
                  <Route path="cart" element={<CartPage />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="help" element={<Help />} />
                  <Route path="help/device" element={<DeviceStatus />} />
                  <Route path="help/barcode-scanner" element={<BarcodeScanner />} />
                  {/*
                    * Inside the shell an unmatched path rendered an empty content area — the same silent
                    * nothing as the old /dashboard, just with the nav still around it. A signed-in customer
                    * following a stale link gets sent somewhere real instead.
                    */}
                  <Route path="*" element={<Navigate to="/app" replace />} />
                </Routes>
                </AppShell>
                </TourProvider>
              </RequireAccount>
            </CartProvider>
          )}
        </Authenticator>
      </ThemeProvider>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/legal/terms" element={<Terms />} />
      <Route path="/legal/privacy" element={<Privacy />} />
      <Route path="/legal/returns" element={<Returns />} />
      {/* TEMPORARY, and necessarily OUTSIDE /app/* — the people who need it cannot sign in. */}
      <Route path="/v1-recovery" element={<V1Recovery />} />
      <Route path="/app/*" element={<ProtectedApp />} />
      {/*
        * Bookmarks from v1, which lived at this same domain. `/dashboard` was its home after sign-in and is
        * the one most customers saved; the catch-all covers the rest of its route table and anything
        * mistyped. Without these, React Router matched nothing and rendered an empty document — the "black
        * screen" customers reported, with nothing on it to say the site had moved.
        */}
      <Route path="/dashboard" element={<Moved />} />
      <Route path="*" element={<Moved />} />
    </Routes>
  );
}
